<template>
  <div class="relative z-0 flex flex-1 h-full max-w-full overflow-hidden bg-light-screen-background dark:bg-dark-screen-background">
    <!-- Left Sidebar -->
    <div class="w-64 border-r border-gray-200 dark:border-dark-divider-border bg-white dark:bg-dark-box-background">
      <div class="h-full flex flex-col">
        <!-- Object Type Filter -->
        <div class="p-4 pb-3 border-b border-gray-200 dark:border-dark-divider-border">
          <ComboBoxInput
            v-model:model-value="selected_object_type"
            :items="utils.SortedObjectTypes()"
            name="object_type"
            classes="w-full relative dark:text-white opacity-0"
            label-attr="label"
            value-attr="type"
            :is-box-background="true"
            :label="$t('Object Type')"
            :tooltip-info="$t('Select object type here')"
            :show-label="false"
            :floating-label="true"
            :allow-null="false" />
        </div>

        <!-- Navigation Tabs -->
        <nav class="flex-1 p-4 space-y-1">
          <button
            v-for="tab in tabs"
            :key="tab"
            class="w-full px-3 py-2 text-left rounded-md text-sm font-medium"
            :class="[
              selected_tab === tab
                ? 'bg-blue-50 text-blue-700 dark:bg-dark-hover-background dark:text-dark-brand-blue'
                : 'text-gray-600 hover:bg-gray-50 dark:text-dark-body-text dark:hover:bg-dark-hover-background'
            ]"
            @click="selected_tab = tab">
            {{ $t(tab) }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col min-w-0 overflow-hidden">
      <!-- Header -->
      <div class="flex-shrink-0 border-b border-gray-200 dark:border-dark-divider-border bg-white dark:bg-dark-box-background">
        <div class="px-6 py-4 flex items-center justify-between">
          <h1 class="text-xl font-semibold text-gray-700 dark:text-dark-body-text">
            {{ $t(selected_tab) }}
          </h1>
          <button
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            @click="createNew">
            <Plus class="w-5 h-5 mr-2" />
            <span>{{ $t(tab_mapper[selected_tab]) }}</span>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-1 overflow-hidden">
        <div class="p-6 overflow-hidden space-y-6">
          <ObjectTypeList v-if="selected_tab === 'Object Types'" :object-types="object_types" :loading="loading_object_types" />
          <Scores
            v-else-if="selected_tab === 'Scores'"
            v-model:show-add-new-score="show_add_new_score"
            :object-type="selected_object_type" />
          <MetricsList
            v-else-if="selected_tab === 'Custom Metrics' || selected_tab === 'Calculated Metrics'"
            :metrics="metrics"
            :metric="current_metric"
            :object-type="selected_object_type"
            :metric-class="selected_tab === 'Custom Metrics' ? 'custom_metric' : 'computed_metric'"
            @update="(object_type) => (selected_object_type = object_type)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Plus } from "lucide-vue-next"
import ComboBoxInput from "@/components/shared/ComboBoxInput.vue"
import { consts } from "@/store/constants"
import { utils } from "@/store/helpers"
import MetricsList from "@/views/dashboard/admin/metrics/MetricsList.vue"
import Scores from "@/views/dashboard/admin/Scores.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import ObjectTypeList from "./metrics/ObjectTypeList.vue"

export default {
  name: "Metrics",
  components: {
    Plus,
    ComboBoxInput,
    MetricsList,
    Scores,
    ObjectTypeList
  },
  inject: ["removeMetricsFromView"],
  data() {
    return {
      utils,
      show_add_new_score: false,
      tabs: ["Custom Metrics", "Calculated Metrics", "Scores", "Object Types"],
      tab_mapper: {
        "Custom Metrics": "Create New Custom Metric",
        "Calculated Metrics": "Create New Computed Metric",
        Scores: "Create New Score",
        "Object Types": "Create New Object Type"
      },
      selected_tab: "Custom Metrics",
      selected_object_type: "company",

      // Object type management
      object_types: [],
      loading_object_types: false,
      show_object_type_form: false,

      metrics_groups: [],
      grouped_operators: {},
      default_metrics: [],
      current_metric: null,
      default_metric_filter: null
    }
  },
  computed: {
    metrics() {
      return this.$store.state.metrics.map((m) => {
        m["display"] = m.name
        m["label"] = m.name
        m["value"] = m.reference_name
        m["type"] = "metric"
        return m
      })
    }
  },
  watch: {
    selected_tab() {
      if (this.selected_tab != "All") {
        this.$store.dispatch("ui/setCurrentAdminBreadcrumb", [this.$t("Metrics"), this.$t(this.selected_tab)])
      }

      history.replaceState(
        history.state,
        "",
        "admin/metrics/?tab=" + utils.slugify(this.selected_tab) + "" + "&o=" + utils.slugify(this.selected_object_type)
      )
    },
    selected_object_type() {
      this.default_metric_filter = null
      history.replaceState(
        history.state,
        "",
        "admin/metrics/?tab=" + utils.slugify(this.selected_tab) + "" + "&o=" + utils.slugify(this.selected_object_type)
      )
    },

    default_metric_filter: {
      deep: true,
      immediate: true,
      handler() {
        this.refilterMetricsList()
      }
    }
  },
  created() {
    this.init()
    this.register_listeners()
    let forecasting_mappings_metrics = []
    let forecasting_mappings =
      utils.find(this.$store.state.settings.modules_settings, { reference_code: "forecasting_mappings" })?.forecasting_mappings || []
    forecasting_mappings.forEach((m) => {
      forecasting_mappings_metrics.push({
        type: "metric",
        data_type: "Number",
        name: this.$t("Renewal Base") + ` (${m.name})`,
        display: this.$t("Renewal Base") + ` (${m.name})`,
        reference_name: `${m.reference_name}_renewal_base`,
        value: `${m.reference_name}_renewal_base`,
        label: this.$t("Renewal Base") + ` (${m.name})`
      })
      forecasting_mappings_metrics.push({
        type: "metric",
        data_type: "Number",
        name: this.$t("Renewal Date") + ` (${m.name})`,
        display: this.$t("Renewal Date") + ` (${m.name})`,
        reference_name: `${m.reference_name}_renewal_date`,
        value: `${m.reference_name}_renewal_date`,
        label: this.$t("Renewal Date") + ` (${m.name})`
      })
    })
    this.grouped_operators = utils.groupBy(consts.FORMULARY_OPTIONS_NO_MORE.concat(forecasting_mappings_metrics), "type")
    this.metrics_groups = utils.groupBy(this.metrics, "object_type")
    let editable_metrics = this.metrics.filter((m) => m.metric_class != "computed_metric" && m.metric_class != "score_metric")
    this.default_metrics = utils.groupBy(editable_metrics, "object_type")
  },
  mounted() {
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", [this.$t("Metrics")])
    if (this.$route.query["tab"]) {
      if (this.tabs.map((x) => utils.slugify(x)).indexOf(this.$route.query["tab"]) != -1) {
        this.selected_tab = this.tabs[this.tabs.map((x) => utils.slugify(x)).indexOf(this.$route.query["tab"])]
      }
    }
    if (this.$route.query["o"]) {
      if (
        utils
          .SortedObjectTypes()
          .map((x) => utils.slugify(x.type))
          .indexOf(this.$route.query["o"]) != -1
      ) {
        this.selected_object_type = this.$route.query["o"]
      } else {
        this.selected_object_type = utils.SortedObjectTypes()[0].type
      }
    }
  },
  activated() {
    this.register_listeners()
    this.$store.dispatch("ui/setCurrentAdminBreadcrumb", [this.$t("Metrics")])
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },
  methods: {
    createNew() {
      if (this.selected_tab === "Custom Metrics") {
        this.current_metric = {
          metric_class: "custom_metric",
          object_type: this.selected_object_type
        }
      } else if (this.selected_tab === "Scores") {
        this.show_add_new_score = true
      } else if (this.selected_tab === "Object Types") {
        this.show_object_type_form = true
      } else {
        this.current_metric = {
          name: null,
          metric_colors: { is_primary_metric_color: false, ranges: [], metric_color_display_option: "dot" },
          id: null,
          input_options: { decimal_places: 2, truncate: true },
          object_type: this.selected_object_type,
          data_type: "Number",
          organization: null,
          metric_type: "t",
          input_type: "Number",
          formulary: {},
          aggregation_method: null,
          metric_class: "computed_metric",
          is_public: true,
          description: null
        }
      }
    },
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.RELOAD_METRICS_AND_SOURCES, () => this.init())
      this.$emitter.on(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS, () => {
        this.metrics_groups = utils.groupBy(this.metrics, "object_type")
        let editable_metrics = this.metrics.filter((m) => m.metric_class != "computed_metric" && m.metric_class != "score_metric")
        this.default_metrics = utils.groupBy(editable_metrics, "object_type")
      })
    },

    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.CUSTOM_METRIC_ADDED_IN_METRICS)
    },

    refilterMetricsList() {
      if (this.default_metric_filter != null && this.default_metric_filter != "") {
        let result = {}
        Object.keys(this.default_metrics).map((key) => {
          let res = this.default_metrics[key].filter((e) => {
            return e["name"].toLowerCase().search(this.default_metric_filter.toLowerCase()) != -1
          })
          if (res.length > 0) {
            result[key] = res
          }
        })
      } else {
        let result = {}
        Object.keys(this.default_metrics).map((key) => {
          let res = this.default_metrics[key]
          if (res.length > 0) {
            result[key] = res
          }
        })
      }
    },

    init() {
      this.grouped_operators = utils.groupBy(consts.FORMULARY_OPTIONS_NO_MORE, "type")
      this.metrics_groups = utils.groupBy(this.metrics, "object_type")
      let editable_metrics = this.metrics.filter((m) => m.metric_class != "computed_metric" && m.metric_class != "score_metric")
      this.default_metrics = utils.groupBy(editable_metrics, "object_type")
    }
  }
}
</script>

<style>
.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20);
  opacity: 0;
}
.scroll-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.scroll-container::-webkit-scrollbar {
  width: 6px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>
